/**
 * Choosing the adapter.
 *
 * The ONLY place in the application that decides which platform it is running
 * on. Every screen takes the resulting `PrintPort` and asks
 * `port.capabilities.silentPdf` rather than branching itself, so the branching
 * exists once instead of once per feature.
 *
 * Detection is by capability rather than by build flag or user-agent. That
 * matters for a specific reason: a broken or missing preload degrades to the
 * browser experience — which works — instead of throwing on the first print and
 * leaving the user with a dead button and no explanation.
 */
import { createBrowserPrintPort, WEB_CAPABILITIES } from './browserPrintPort';
import { createElectronPrintPort, electronBridge, ELECTRON_CAPABILITIES } from './electronPrintPort';
import type { PrintPort } from './printPort';

export interface CreatePrintPortOptions {
  /** Force an adapter. For tests only; production detects. */
  readonly force?: 'web' | 'electron';
}

/**
 * @returns the Electron port when the preload bridge is present, otherwise the
 *          browser port. Never throws.
 */
export function createPrintPort(opt: CreatePrintPortOptions = {}): PrintPort {
  if (opt.force === 'web') return createBrowserPrintPort();
  if (opt.force === 'electron') {
    const bridge = electronBridge();
    if (!bridge) {
      // Asked for Electron in a browser. Returning the browser port is
      // dishonest about capabilities, so this throws instead — a caller that
      // demanded Electron needs to know it did not get it.
      throw new Error('createPrintPort({force:"electron"}) but no preload bridge is present.');
    }
    return createElectronPrintPort(bridge);
  }

  const bridge = electronBridge();
  return bridge ? createElectronPrintPort(bridge) : createBrowserPrintPort();
}

/** What the current process can do, without constructing an adapter. */
export function currentPrintCapabilities(): PrintPort['capabilities'] {
  return electronBridge() ? ELECTRON_CAPABILITIES : WEB_CAPABILITIES;
}

export { WEB_CAPABILITIES, ELECTRON_CAPABILITIES };
export type { PrintPort } from './printPort';
